// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { useSwiper } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.context";
import {
    ArrowPosition,
    ArrowType,
    NavigationProps,
    SwiperInstance,
} from "@plumberssupply/App/Components/XngCarousel/XngCarousel.models";
import IconArrowLeft from "@plumberssupply/XDL/Icons/IconArrowLeft";
import IconArrowRight from "@plumberssupply/XDL/Icons/IconArrowRight";
import IconPaginationLeft from "@plumberssupply/XDL/Icons/IconPaginationLeft";
import IconPaginationRight from "@plumberssupply/XDL/Icons/IconPaginationRight";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition, showSecondaryArrows: boolean) => {
    const factor = arrowPosition === "left" ? -1 : 1;
    return {
        size: 20,
        css: css`
            position: static;
            margin-${arrowPosition === "left" ? `right` : `left`}: 20px;
            background: transparent;
            height: 25px;
            width: 25px;
            & > ${IconWrapper} {
                color: ${color("common.ui8")};
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
                &:hover {
                    color: ${color("secondary")};
                }
            }
            transform: none;
            z-index: 10;
            padding: 0;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;

            &:focus {
                box-shadow: none;
            }

            ${
                (arrowPosition === "top" || arrowPosition === "bottom") &&
                css`
                    position: relative;
                    inset: 0;
                    transform: none;
                    margin: auto;
                `
            };

            ${
                showSecondaryArrows === true &&
                css`
                    position: absolute;
                    height: 32px;
                    width: 32px;
                    top: 52px;

                    ${arrowPosition === "left" &&
                    css`
                        left: 0;
                    `};
                    ${arrowPosition === "right" &&
                    css`
                        right: 0;
                    `};
                `
            };
            
            &.swiper-button-disabled {
                & > ${IconWrapper} {
                    color: ${color("common.ui8", 0.33)};
                }
            }

            ${({ showOnMobile }: any) =>
                !showOnMobile &&
                css`
                    ${xScreen("lte", "md")} {
                        display: none;
                    }
                `}
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 20,
    },
    bottom: {
        src: ArrowDown,
        size: 20,
    },
    left: {
        src: IconPaginationLeft,
        size: 20,
    },
    right: {
        src: IconPaginationRight,
        size: 20,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const getArrowElement = ({
    swiper,
    showOnMobile,
    arrowType,
}: {
    swiper: SwiperInstance | null;
    arrowType: string;
    showOnMobile?: boolean;
}) => {
    const arrowElement = arrowType === "next" ? swiper?.navigation.nextEl : swiper?.navigation.prevEl;

    if (!swiper || !arrowElement) {
        return {
            className: "",
            element: undefined,
        };
    }

    const navigationSwiperClassName = arrowElement.className.replace(`swiper-button-${arrowType}`, "").trim();

    const classNames = [
        `arrow-button xng-carousel-${arrowType}`,
        `${showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop"}`,
        navigationSwiperClassName,
    ];

    return {
        className: classNames.join(" "),
        disabled: arrowElement.className.indexOf("swiper-button-disabled") > -1,
        element: arrowElement,
    };
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, showSecondaryArrows, productImageCarousel, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        let { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        if (showSecondaryArrows && !productImageCarousel) {
            if (arrowPosition === "left") {
                iconSrc = IconArrowLeft;
            }
            if (arrowPosition === "right") {
                iconSrc = IconArrowRight;
            }
            iconSize = 32;
        }

        const [wrapperSize, setWrapperSize] = useState<{ height?: number; width?: number }>({});
        const [resizeObserveInitiated, setResizeObserveInitiated] = useState(false);

        const {
            className,
            element: arrowElement,
            disabled,
        } = useMemo(
            () =>
                getArrowElement({
                    swiper: swiperInstance,
                    showOnMobile,
                    arrowType,
                }),
            [swiperInstance],
        );

        useEffect(() => {
            if (!swiperInstance?.wrapperEl || resizeObserveInitiated) {
                return;
            }

            // To update when a wrapper height change
            setResizeObserveInitiated(true);
            const observer = new (window as any).ResizeObserver(([entry]: any) => setWrapperSize(entry.contentRect));
            observer.observe(swiperInstance.wrapperEl);
            return () => observer.disconnect();
        }, [swiperInstance]);

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [swiperInstance],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            wrapperHeight: wrapperSize.height,
            arrowPosition,
            showOnMobile,
            className: `${className} ${otherProps.className || ""}`.trim(),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition, showSecondaryArrows!)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
